'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  useCarousel,
} from '@lastbrand/web-ui/carousel'
import { ChevronLeftIcon, ChevronRightIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Image } from '@src/quince-components/src/atoms'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { DS_LG_MEDIA_QUERY } from '@src/quince-site-common/src/constants/app'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { isProductImage } from '@src/quince-site-common/src/utils/typeGuards'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { useProductImages } from '../productImages/hooks/use-product-images'

const DRAG_THRESHOLD_PX = 10

const DESKTOP_VISIBLE_SLIDES = 2
// Arrows only render at `lg`, where two slides are visible — match the legacy Swiper's
// `slidesPerGroup={numberOfVisibleSlides}` so an arrow click advances a full page.
const CAROUSEL_BREAKPOINTS = {
  [DS_LG_MEDIA_QUERY]: { slidesToScroll: DESKTOP_VISIBLE_SLIDES },
}

const containerClass = css({
  width: 'auto',
  marginInline: 'calc(var(--web-ui-spacing-space-16) * -1)',
  marginTop: 0,
  marginBottom: 0,
  // 20px block padding per the Figma mini-PDP mobile layout (net-new top inset vs the
  // legacy marginTop 0 / marginBottom 16 rhythm — intentional).
  paddingBlock: 'space-20',
  lg: { width: '46%', marginInline: 0, marginTop: 'space-32', marginBottom: '0', paddingBlock: 0 },
})

const ARROW_ICON_SIZE = '20px'

const carouselCss = css.raw({ position: 'relative' })

// Below `lg` the gallery is a fixed 160px-wide filmstrip (DS 2.0 Widget-ProductGalleryMini);
// at `lg` it becomes a 2-up grid.
const MOBILE_SLIDE_WIDTH = '160px'

const contentCss = css.raw({
  gap: 'space-8',
  '& [data-slot="carousel-item"]': {
    minWidth: '0',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: MOBILE_SLIDE_WIDTH,
    lg: { flexBasis: 'calc((100% - var(--web-ui-spacing-space-8)) / 2)' },
  },
  // Edge insets ride on the track, so the 16px only shows once there's nothing left to scroll to on
  // that side. Embla measures the two ends differently: the start gap geometrically (and
  // `ScrollContain` pins the first snap to 0, keeping it on screen at rest), the end gap strictly as
  // the last slide's computed `margin-right`, which it folds into `contentSize`. Track padding would
  // be read for neither, so both insets sit on the items.
  '& [data-slot="carousel-item"]:first-child': {
    marginLeft: 'space-16',
    lg: { marginLeft: 0 },
  },
  '& [data-slot="carousel-item"]:last-child': {
    marginRight: 'space-16',
    lg: { marginRight: 0 },
  },
})

const slideClass = css({
  cursor: 'pointer',
  display: 'block',
  width: '100%',
  aspectRatio: '4/5',
  padding: 0,
  border: 0,
  bg: 'transparent',
})

const carouselControlButtonCss = css.raw({
  position: 'absolute',
  top: '50%',
  zIndex: 1,
  display: { base: 'none', lg: 'inline-flex' },
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'space-8',
  borderRadius: 'radius-round',
  bg: 'white',
  boxShadow: 'shadow-below',
  color: 'darkgray',
  transition: 'color 150ms ease-out, background-color 150ms ease-out',
  _hover: { bg: 'paperwhite' },
  _active: { bg: 'offwhite' },
  _focusVisible: {
    outlineWidth: '2px',
    outlineOffset: '0.25rem',
    outlineColor: 'black',
    outlineStyle: 'solid',
  },
})

const prevArrowCss = css.raw(carouselControlButtonCss, { left: '0', transform: 'translate(-50%, -50%)' })
const nextArrowCss = css.raw(carouselControlButtonCss, { right: '0', transform: 'translate(50%, -50%)' })

function MiniPDPCarouselControls() {
  const { t } = useTranslation('common')
  const { canScrollPrev, canScrollNext } = useCarousel()

  return (
    <>
      {canScrollPrev && (
        <CarouselPreviousButton asChild css={prevArrowCss} aria-label={t('previous')}>
          <button type="button">
            <ChevronLeftIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
          </button>
        </CarouselPreviousButton>
      )}
      {canScrollNext && (
        <CarouselNextButton asChild css={nextArrowCss} aria-label={t('next')}>
          <button type="button">
            <ChevronRightIcon width={ARROW_ICON_SIZE} height={ARROW_ICON_SIZE} />
          </button>
        </CarouselNextButton>
      )}
    </>
  )
}

export const MiniPDPProductImagesDS = ({
  product,
  sectionData,
}: {
  product: Product
  sectionData: TPDPSectionData
}) => {
  const { t } = useTranslation('common')
  const { productMedia, selectedOptions } = useProductImages(product, sectionData)
  const productImages = productMedia.filter(media => isProductImage(media))
  const router = useRouter()

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  const productLink = getProductLink(product, selectedOptions)

  // Embla drags on pointer input only, so trackpad/wheel scroll needs the wheel-gestures plugin —
  // same fix as the PDP gallery (STRFRNT-16402). One instance per carousel — it holds state.
  const wheelPlugins = useMemo(() => [WheelGesturesPlugin()], [])

  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const draggedRef = useRef(false)

  const handlePointerDownCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY }
    draggedRef.current = false
  }, [])

  const handlePointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    if (!start) return
    if (
      Math.abs(event.clientX - start.x) > DRAG_THRESHOLD_PX ||
      Math.abs(event.clientY - start.y) > DRAG_THRESHOLD_PX
    ) {
      draggedRef.current = true
    }
  }, [])

  const handleImageClick = useCallback(() => {
    if (draggedRef.current) {
      draggedRef.current = false
      return
    }
    router.push(productLink)
  }, [router, productLink])

  return (
    <div className={containerClass} data-testid={TEST_LOCATORS.MINI_PDP.PRODUCT_IMAGES}>
      <Carousel
        css={carouselCss}
        opts={{ align: 'start', containScroll: 'trimSnaps', breakpoints: CAROUSEL_BREAKPOINTS }}
        plugins={wheelPlugins}
        onPointerDownCapture={handlePointerDownCapture}
        onPointerMoveCapture={handlePointerMoveCapture}
      >
        <CarouselContent css={contentCss}>
          {productImages.map((productImage, index) => (
            <CarouselItem key={productImage.image.id}>
              <button
                type="button"
                data-testid={TEST_LOCATORS.MINI_PDP.IMAGE(index + 1)}
                className={slideClass}
                onClick={handleImageClick}
                aria-label={t('view_product_details')}
              >
                <Image
                  {...ProductImage.makeImageProps(productImage)}
                  loading={isMobile ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 || index === 1 ? 'high' : 'auto'}
                  alt={productImage.image.title}
                />
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
        <MiniPDPCarouselControls />
      </Carousel>
    </div>
  )
}
